import { Menu } from "antd"
import React from "react"
import { useTranslation } from "react-i18next"
import { Navigate, Outlet, useLocation, useNavigate } from "react-router-dom"

import { RoutesPaths } from "../../../constants/routes.ts"
import {
  type CommunicationRights,
  useCommunicationRights
} from "../communication-permissions.ts"

interface SettingsPage {
  key: string
  labelKey: string
  allowed: (rights: CommunicationRights) => boolean
}

// A settings page joins this list when its screen ships.
export const SETTINGS_PAGES: SettingsPage[] = [
  {
    key: RoutesPaths.communication.settingsEmail,
    labelKey: "communication.settings.email.nav",
    allowed: (rights) => rights.settings
  },
  {
    key: RoutesPaths.communication.settingsSendingRules,
    labelKey: "communication.settings.rules.nav",
    allowed: (rights) => rights.settings
  },
  {
    key: RoutesPaths.communication.settingsSms,
    labelKey: "communication.settings.sms.nav",
    allowed: (rights) => rights.settings
  },
  {
    key: RoutesPaths.communication.settingsTemplates,
    labelKey: "communication.templates.nav",
    allowed: (rights) => rights.templates
  }
]

export const useSettingsPages = () => {
  const rights = useCommunicationRights()
  return SETTINGS_PAGES.filter((page) => page.allowed(rights))
}

export const FirstSettingsPage = () => {
  const pages = useSettingsPages()
  return pages.length ? <Navigate to={pages[0].key} replace /> : null
}

const CommunicationSettingsLayout = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { pathname } = useLocation()
  const pages = useSettingsPages()

  return (
    <div className="flex flex-col gap-4 md:flex-row">
      <nav
        aria-label={t("communication.settings.title")}
        className="md:w-56 md:shrink-0"
      >
        <Menu
          mode="inline"
          selectedKeys={[pathname]}
          onClick={({ key }) => navigate(key)}
          items={pages.map((page) => ({
            key: page.key,
            label: t(page.labelKey)
          }))}
        />
      </nav>
      <section className="min-w-0 flex-1">
        <Outlet />
      </section>
    </div>
  )
}

export default CommunicationSettingsLayout
